import { Button, Tabs, Tab, TextField, Paper, } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

export const Container = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  overflow: 'auto',
});

export const SectionTitle = styled('h2')({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--primary-main)',
  marginBottom: '10px',
});

export const DocumentsSection = styled('div')({
  display: 'flex',
  gap: '5px',
  flex: 1,
  
  '@media (max-width: 1024px)': {
    flexDirection: 'column',
  },
});

export const DocumentList = styled(Paper)({
  width: '300px',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  height: 'fit-content',
  maxHeight: '600px',
  overflowY: 'auto',
  
  '@media (max-width: 1024px)': {
    width: '100%',
    maxHeight: '300px',
  },
});

export const DocumentItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ theme, isActive }) => ({
  padding: '12px 15px',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  backgroundColor: isActive
    ? alpha(theme.palette.secondary.main, 0.1)
    : 'transparent',
  border: isActive
    ? `1px solid ${theme.palette.secondary.main}`
    : '1px solid rgba(0, 0, 0, 0.1)',
  
  '&:hover': {
    backgroundColor: isActive
      ? alpha(theme.palette.secondary.dark, 0.1)
      : 'rgba(0, 0, 0, 0.03)',
  },
}));

export const DocumentTitle = styled('div')({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const EditorContainer = styled(Paper)({
  flex: 1,
  padding: '25px',
  display: 'flex',
  flexDirection: 'row',
  gap: '20px',
});

export const EditorTabs = styled(Tabs)({
  marginBottom: '15px',
  
  '& .MuiTabs-indicator': {
    backgroundColor: 'var(--secondary-main)',
  },
});

export const EditorTab = styled(Tab)({
  '&.Mui-selected': {
    color: 'var(--secondary-main)',
  },
});

export const TextEditor = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
});

export const BoldButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ isActive }) => ({
  backgroundColor: isActive ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
  minWidth: 'unset',
  padding: '5px 10px',
}));

export const ItalicButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ isActive }) => ({
  backgroundColor: isActive ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
  minWidth: 'unset',
  padding: '5px 10px',
}));

export const UnderlineButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ isActive }) => ({
  backgroundColor: isActive ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
  minWidth: 'unset',
  padding: '5px 10px',
}));

export const ToolbarButton = styled(Button)({
  minWidth: 'unset',
  padding: '5px 10px',
});

export const Divider = styled('div')({
  width: '1px',
  height: '24px',
  backgroundColor: 'rgba(0, 0, 0, 0.1)',
  margin: '0 5px',
});

export const EditorToolbar = styled('div')({
  display: 'flex',
  gap: '5px',
  padding: '10px',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '4px',
  flexWrap: 'wrap',
});

export const EditorContent = styled(TextField)({
  flex: 1,
  
  '& .MuiInputBase-root': {
    height: '100%',
  },
  
  '& .MuiInputBase-input': {
    height: '100%',
    fontFamily: 'Georgia, serif',
    fontSize: '1rem',
    lineHeight: '1.6',
  },
});

export const VersionHistoryItem = styled('div')({
  padding: '15px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  
  '&:last-child': {
    borderBottom: 'none',
  },
});

export const VersionHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

export const VersionTitle = styled('span')({
  fontWeight: 500,
});

export const VersionTimestamp = styled('span')({
  fontSize: '0.8rem',
  color: 'rgba(0, 0, 0, 0.5)',
});

export const VersionDescription = styled('p')({
  fontSize: '0.9rem',
  color: 'rgba(0, 0, 0, 0.7)',
  margin: '5px 0',
});

export const VersionActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  marginTop: '10px',
});

export const DropdownButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main
}));

export const ResearchPanelWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isCollapsed'
})(({ isCollapsed }) => ({
  flexGrow: 0,
  flexShrink: 0,
  flexBasis: isCollapsed ? '0' : '50%',
  maxWidth: isCollapsed ? '0' : '50%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  transition: 'flex-basis 225ms cubic-bezier(0.4, 0, 0.6, 1), max-width 225ms cubic-bezier(0.4, 0, 0.6, 1)',

  '& > .MuiCollapse-root': {
    minWidth: isCollapsed ? '0' : '300px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: isCollapsed ? 'hidden' : 'visible',
  },
  
  '& > .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner > div': {
     height: '100%',
     display: 'flex',
     flexDirection: 'column',
  }
}));